import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import doneIcon from '../assets/images/done.svg';
import { useClubStore } from '../store/clubStore';
import axiosInstance from '../axiosConfig';
import { useGlobalStore } from '../store/globalStore';
import { useDialogStore } from '../store/dialogStore';

const EditDataPage: React.FC = () => {
  const myClub = useClubStore((state) => state.myClub);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const openDialog = useDialogStore((state) => state.openDialog);

  const [form, setForm] = useState({
    email: '',
    password: '',
    iban: '',
    bic: '',
  });

  // --- Helpers: Sanitize & Validate ---
  const sanitizeIban = (value: string): string => {
    // Remove spaces, force uppercase, allow only A-Z and 0-9, trim to max 34 chars
    const cleaned = value.replace(/\s+/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return cleaned.slice(0, 34);
  };

  const sanitizeBic = (value: string): string => {
    // BIC allows only A-Z and 0-9, length 8 or 11
    const cleaned = value.replace(/\s+/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return cleaned.slice(0, 11);
  };

  // IBAN checksum (ISO 13616, mod 97 == 1)
  const validateIban = (ibanRaw: string): boolean => {
    const iban = sanitizeIban(ibanRaw);
    if (!iban) return true; // empty treated as valid for optional input
    // Basic length check (15..34). For DE specifically 22.
    if (iban.startsWith('DE') && iban.length !== 22) return false;
    if (iban.length < 15 || iban.length > 34) return false;
    // Move first 4 chars to end
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    // Replace letters with numbers A=10..Z=35 and compute mod 97 using chunking
    let remainder = 0;
    for (let i = 0; i < rearranged.length; i++) {
      const ch = rearranged[i];
      const value = ch >= 'A' && ch <= 'Z' ? (ch.charCodeAt(0) - 55).toString() : ch;
      for (let j = 0; j < value.length; j++) {
        remainder = (remainder * 10 + Number(value[j])) % 97;
      }
    }
    return remainder === 1;
  };

  const validateBic = (bicRaw: string): boolean => {
    const bic = sanitizeBic(bicRaw);
    if (!bic) return true; // empty treated as valid for optional input
    // 8 or 11 chars, pattern: 4 letters (bank) + 2 letters (country) + 2 alnum (location) + optional 3 alnum (branch)
    const re = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    return re.test(bic) && (bic.length === 8 || bic.length === 11);
  };

  useEffect(() => {
    if (myClub) {
      setForm({
        email: myClub.email || '',
        password: '',
        iban: myClub.bank?.iban || '',
        bic: myClub.bank?.bic || '',
      });
    }
  }, [myClub]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    let nextValue = value;
    if (id === 'iban') nextValue = sanitizeIban(value);
    if (id === 'bic') nextValue = sanitizeBic(value);
    setForm((prevForm) => ({
      ...prevForm,
      [id]: nextValue,
    }));
  };

  const ibanValid = validateIban(form.iban);
  const bicValid = validateBic(form.bic);
  const canSubmit = (!form.iban || ibanValid) && (!form.bic || bicValid);

  const submit = async () => {
    if (!canSubmit) {
      openDialog('Bitte korrigieren Sie die IBAN/BIC-Eingaben.', () => { });
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.put('/club', form);
      openDialog(
        <div className="py-5">
          <img className="mx-auto mb-2" src={doneIcon} alt="" />
          <div className="text-md text-center">Erfolgreich aktualisiert.</div>
        </div>,
        () => { },
        true,
      );
    } catch (error) {
      console.error('Error logging in:', error);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  return (
    <div className="grid gap-2 items-center mx-5 bg-white p-4 border rounded-md">
      <div className="font-medium text-lg mb-4">Daten ändern</div>

      <div className="flex gap-4 pb-3">
        <div>
          <div className="font-medium text-lg"> {myClub?.name}</div>
          <div> {myClub?.sender_name}</div>
        </div>
      </div>
      <div className="flex gap-4 items-center">
        <div className="w-5 hidden sm:inline-block">🔢</div>
        <div className="w-28">Clubnummer</div>
        <div className="font-medium"> {myClub?.club_number}</div>
      </div>
      <div className="h-4 w-full mt-4 border-t"></div>
      <div className="flex  gap-4 items-center">
        <div className="w-5 hidden sm:inline-block">✉️</div>
        <div className="w-28">E-Mail</div>
        <div>{form.email}</div>
      </div>
      <div className="flex gap-4 items-center">
        <div className="w-5 hidden sm:inline-block">💳</div>
        <div className="w-28">IBAN</div>
        <div>
          <Input
            type="string"
            id="iban"
            placeholder=""
            value={form.iban}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === ' ') e.preventDefault();
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text');
              setForm((prev) => ({ ...prev, iban: sanitizeIban(text) }));
            }}
            className={`${form.iban && !ibanValid ? 'border-red-500' : ''}`}
          />
          {form.iban && !ibanValid && (
            <div className="mt-1 text-xs text-red-600">Das Format der IBAN ist ungültig. Bitte im korrekten Format eingeben.</div>
          )}
        </div>
      </div>
      <div className="flex gap-4 items-center">
        <div className="w-5 hidden sm:inline-block">💵</div>
        <div className="w-28">BIC</div>
        <div>
          <Input
            type="string"
            id="bic"
            placeholder=""
            value={form.bic}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === ' ') e.preventDefault();
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text');
              setForm((prev) => ({ ...prev, bic: sanitizeBic(text) }));
            }}
            className={`${form.bic && !bicValid ? 'border-red-500' : ''}`}
          />
          {form.bic && !bicValid && (
            <div className="mt-1 text-xs text-red-600">Das Format des BIC ist ungültig. Bitte im korrekten Format eingeben.</div>
          )}
        </div>
      </div>
      <div>
        <Button onClick={() => submit()} type="submit" disabled={!canSubmit}>
          Daten speichern
        </Button>
      </div>
    </div>
  );
};

export default EditDataPage;
